import React from "react";
import { FaList, FaSyncAlt, FaHistory, FaBolt, FaWifi } from "react-icons/fa";
import "./RailtelPlanCard.css";

/**
 * `subscriber` shape (from railtelLookupByUsername — see api/railtelApi.js):
 * { subscriberid, username, name, mobile, email, address, package,
 *   sufficient_balance_notice, expiry, balance, online_status, is_active }
 *
 * `sufficient_balance_notice` is set (and the Top-up button hidden in
 * favor of showing this text) specifically during advance recharge, when
 * the provider's own package_name field carries a message saying the
 * subscriber already has enough balance queued — pulled out server-side
 * from the same raw field `package` comes from, see
 * railwireParsePackageName() in railwire_client.php.
 *
 * Simpler than the old version — the provider's new lookup endpoint
 * returns package/expiry/balance/address directly in one call, so there's
 * no more separate `details`/`dataUsage` derived from a scraped usage
 * table. One casualty of that: there's no reliable "X of Y GB used
 * against your package" figure anymore — the new API only exposes
 * session-level upload/download totals (see Session History), not usage
 * against a billing-cycle quota, so that row's been dropped rather than
 * showing something misleading.
 *
 * `is_active` comes pre-computed from the backend (expiry >= today) —
 * the provider's own `status`/`sub_status` fields come back null even
 * for accounts that are clearly working, so they're not reliable to
 * badge "active" off of directly.
 */
const RailtelPlanCard = ({
  subscriber,
  isOnline,
  onlineLoading,
  refreshLoading,
  onRefresh,
  onRecharge,
  onSessionHistory,
  rechargeLoading,
}) => {
  if (!subscriber) return null;

  return (
    <div className="railtel-card">
      <div className="railtel-card__header">
        <FaList className="railtel-card__header-icon" />
        <span>Subscriber Details</span>
        <button
          className="railtel-card__refresh-btn"
          onClick={onRefresh}
          disabled={refreshLoading}
          title="Refresh from RailTel"
        >
          {onlineLoading ? (
            <span className="railtel-badge railtel-badge--muted">Checking…</span>
          ) : (
            <span className={`railtel-badge ${isOnline ? "railtel-badge--online" : "railtel-badge--offline"}`}>
              {isOnline ? (
                <div className="railtel-card__refresh-row">
                  <FaWifi style={{ marginRight: 5 }} />
                  <p style={{ marginRight: 5 }} >Online</p>
                  <FaSyncAlt className={refreshLoading ? "spin" : ""} /> 
                </div>
              ) : (
                <div className="railtel-card__refresh-row">
                  <FaWifi style={{ marginRight: 5 }} />
                  <p style={{ marginRight: 5 }} >Offline</p>
                  <FaSyncAlt className={refreshLoading ? "spin" : ""} /> 
                </div>
              )}
            </span>
          )}
          {/* <FaSyncAlt className={refreshLoading ? "spin" : ""} /> */}
        </button>
      </div>

      <table className="railtel-card__table">
        <tbody>
          <tr>
            <td className="railtel-card__label">Name</td>
            <td className="railtel-card__value railtel-card__value--link">
              {subscriber.name || "—"}
            </td>
          </tr>
          <tr>
            <td className="railtel-card__label">Mobile No.</td>
            <td className="railtel-card__value railtel-card__value--link">
              {subscriber.mobile || "—"}
            </td>
          </tr>
          {subscriber.email && (
            <tr>
              <td className="railtel-card__label">Email</td>
              <td className="railtel-card__value railtel-card__value--link">
                {subscriber.email}
              </td>
            </tr>
          )}
          {subscriber.address && (
            <tr>
              <td className="railtel-card__label">Address</td>
              <td className="railtel-card__value railtel-card__value--link">
                {subscriber.address}
              </td>
            </tr>
          )}

          <tr className="railtel-card__section-row">
            <td colSpan={2}>Subscription Data</td>
          </tr>

          <tr>
            <td className="railtel-card__label">Username</td>
            <td className={`railtel-card__value ${subscriber.is_active ? "railtel-card__value--accent" : "railtel-card__value--red"}`}>
              {subscriber.username}
            </td>
          </tr>
          <tr>
            <td className="railtel-card__label">Subscriber ID</td>
            <td className={`railtel-card__value ${subscriber.is_active ? "railtel-card__value--accent" : "railtel-card__value--red"}`}>
              {subscriber.subscriberid}
            </td>
          </tr>
          <tr>
            <td className="railtel-card__label">Subscription Expiry</td>
            <td className={`railtel-card__value ${subscriber.is_active ? "railtel-card__value--accent" : "railtel-card__value--red"}`}>
              {subscriber.expiry || "—"}
            </td>
          </tr>
          <tr>
            <td className="railtel-card__label">Package</td>
            <td className="railtel-card__value railtel-card__value--link">
              {subscriber.package || "—"}
            </td>
          </tr>

          <tr>
            <td className="railtel-card__label">Status</td>
            <td className="railtel-card__value">
              <span className={`railtel-badge ${subscriber.is_active ? "railtel-badge--active" : "railtel-badge--inactive"}`}>
                {subscriber.is_active ? "Active" : "Expired"}
              </span>
            </td>
          </tr>

          <tr>
            <td className="railtel-card__label">Connection Status</td>
            <td className="railtel-card__value">
              {onlineLoading ? (
                <span className="railtel-badge railtel-badge--muted">Checking…</span>
              ) : (
                <span className={`railtel-badge ${isOnline ? "railtel-badge--online" : "railtel-badge--offline"}`}>
                  <FaWifi style={{ marginRight: 5 }} />
                  {isOnline ? "Online" : "Offline"}
                </span>
              )}
            </td>
          </tr>

          <tr>
            <td className="railtel-card__label">Account Balance</td>
            <td className="railtel-card__value railtel-card__value--link">
              ₹{subscriber.balance ?? "0.00"}
            </td>
          </tr>
        </tbody>
      </table>

      <div className="railtel-card__actions">
        <button
          className="railtel-btn railtel-btn--outline"
          onClick={onSessionHistory}
        >
          <FaHistory style={{ marginRight: 6 }} />
          Session History
        </button>
        {subscriber.sufficient_balance_notice ? (
          // The provider itself is saying a top-up isn't needed — the
          // subscriber already has enough balance queued for their next
          // renewal. Showing the button anyway would just invite a
          // pointless recharge attempt.
          <span className="railtel-sufficient-balance-notice">
            {subscriber.sufficient_balance_notice}
          </span>
        ) : (
          <button
            className="railtel-btn railtel-btn--primary"
            onClick={onRecharge}
            disabled={rechargeLoading}
          >
            <FaBolt style={{ marginRight: 6 }} />
            {rechargeLoading ? "Processing…" : "Top-up Subscriber Account"}
          </button>
        )}
      </div>
    </div>
  );
};

export default RailtelPlanCard;
